import React from "react";

import skyline from "../../assets/backgrounds/skyline.webp";

export interface AuroraBackgroundProps {
  /**
   * Extra classes for the container. NOTE: this component does not set its own
   * `position` — pass `absolute inset-0` or `fixed inset-0` from the caller.
   */
  className?: string;
  /** Dim the whole effect (0–1). Lower = subtler. */
  intensity?: number;
  /** Opacity of the aurora colour tint drifting over the photo (0–1). */
  auroraOpacity?: number;
  /** Opacity of the skyline photo base layer (0–1). Lower = aurora shows more. */
  skylineOpacity?: number;
  /** Hide the skyline photo and render aurora only. */
  hideSkyline?: boolean;
}

// Tiny tiling noise texture (base64 SVG) for a filmic grain overlay.
const GRAIN =
  "data:image/svg+xml;base64," +
  btoa(
    `<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'>
      <filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter>
      <rect width='100%' height='100%' filter='url(#n)' opacity='0.5'/>
    </svg>`
  );

/**
 * Chicago-skyline backdrop with an animated aurora tint. The night skyline
 * photo is the fixed base layer; blurred blobs (blue + amber) drift slowly over
 * it as coloured atmospheric light, finished with a grain overlay and a dark
 * scrim/vignette so foreground content stays legible over the photo. Motion is
 * disabled under `prefers-reduced-motion` via the `motion-reduce:` variants
 * (blobs stay put but still render).
 */
const AuroraBackground: React.FC<AuroraBackgroundProps> = ({
  className = "",
  intensity = 1,
  auroraOpacity = 1,
  skylineOpacity = 0.45,
  hideSkyline = false,
}) => {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none overflow-hidden bg-gray-950 ${className}`}
      style={{ opacity: intensity }}
    >
      {/* Skyline photo base layer */}
      {!hideSkyline && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url("${skyline}")`, opacity: skylineOpacity }}
        />
      )}

      {/* Deep base wash (behind the photo when shown; the whole backdrop when
          hidden). Also darkens the photo slightly for contrast. */}
      <div
        className={`absolute inset-0 bg-gradient-to-b from-gray-950/75 via-gray-950/45 to-gray-950/85 ${
          hideSkyline ? "opacity-100" : ""
        }`}
      />

      {/* Drifting aurora blobs — coloured light over the city */}
      <div
        className="absolute inset-0 mix-blend-screen"
        style={{ opacity: auroraOpacity }}
      >
        <div
          className="absolute -top-1/4 -left-1/5 h-[80vh] w-[80vh] rounded-full blur-[64px] animate-aurora-slow motion-reduce:animate-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(56,120,255,0.8), rgba(37,99,235,0) 70%)",
            willChange: "transform, opacity",
          }}
        />
        <div
          className="absolute top-1/4 -right-1/5 h-[72vh] w-[72vh] rounded-full blur-[70px] animate-aurora-slower motion-reduce:animate-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(245,178,61,0.55), rgba(245,178,61,0) 70%)",
            willChange: "transform, opacity",
          }}
        />
        <div
          className="absolute -bottom-1/4 left-1/3 h-[70vh] w-[70vh] rounded-full blur-[76px] animate-aurora-slow motion-reduce:animate-none"
          style={{
            animationDelay: "-8s",
            background:
              "radial-gradient(circle at center, rgba(72,140,255,0.6), rgba(30,41,89,0) 70%)",
            willChange: "transform, opacity",
          }}
        />
      </div>

      {/* Grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-soft-light"
        style={{ backgroundImage: `url("${GRAIN}")`, backgroundSize: "160px" }}
      />

      {/* Vignette to keep edges dark and content legible */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, transparent 45%, rgba(7,10,18,0.6) 100%)",
        }}
      />
    </div>
  );
};

export default AuroraBackground;
